import { getServiceUrl } from '@airclou/utils';
import { HTTP_TIMEOUT_MS } from '../config';
import { apiErrorFrom } from './errors';

const API_BASE_URL = getServiceUrl('dashboard');

export interface App {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  status: 'active' | 'inactive' | 'maintenance';
  created_at: string;
  updated_at: string;
}

export interface AppUser {
  user_id: string;
  role: string;
  joined_at: string;
  user?: {
    email: string;
    name: string;
  };
  workspace_id?: string;
  workspace_name?: string;
}

// Get auth token from localStorage
function getAuthToken(): string | null {
  return localStorage.getItem('dashboard:authToken');
}

// List all apps
export async function listApps(): Promise<App[]> {
  const response = await fetch(`${API_BASE_URL}/api/apps`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch apps');
  }

  const json = await response.json();
  return json.data;
}

// Get single app details
export async function getApp(appId: string): Promise<App> {
  const response = await fetch(`${API_BASE_URL}/api/apps/${appId}`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
  });

  if (!response.ok) {
    // Carries the status so the detail page can tell "this app is gone" from
    // "the request failed", and the server's message instead of a generic string.
    throw await apiErrorFrom(response, 'Failed to fetch app');
  }

  const json = await response.json();
  return json.data;
}

// Get users with access to an app
export async function listAppUsers(appId: string): Promise<AppUser[]> {
  const response = await fetch(`${API_BASE_URL}/api/apps/${appId}/users`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch app users');
  }

  const json = await response.json();
  return json.data;
}
